import { useCallback, useEffect, useRef, useState } from 'react'
import { reportAPI } from '@/lib/api/reports'
import type { ReportVersionView, ReportViewWithVersions } from '@/lib/types/report'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import { useReportLive } from '@/hooks/report/useReportLive'
import { changedSectionIds } from '@/lib/report/versionDiff'
import { ReportViewer } from '@/components/report/ReportViewer'

/** Full live report document: fetch + poll + socket-event refresh + version scrubbing.
 *  Socket CONNECTION is the mount's job — this only consumes the events array. */
export function LiveReportDocument({ reportId, events }: { reportId: string; events?: ToolCallEvent[] }) {
  const fetchView = useCallback(async (): Promise<ReportViewWithVersions> => {
    const [viewRes, versionsRes] = await Promise.all([
      reportAPI.getReport(reportId),
      reportAPI.getReportVersions(reportId),
    ])
    return { ...viewRes.data, versions: versionsRes.data.versions }
  }, [reportId])

  const { view, error, writingSection, isLive } = useReportLive({ fetchView, reportId, events })

  const [selected, setSelected] = useState<number | null>(null) // null = follow head
  const [snapshot, setSnapshot] = useState<ReportVersionView | null>(null)
  const prevHeadRef = useRef(0)
  const [changedIds, setChangedIds] = useState<string[]>([])

  useEffect(() => {
    if (!view) return
    if (prevHeadRef.current > 0 && view.version > prevHeadRef.current) {
      setChangedIds(changedSectionIds(view.versions, prevHeadRef.current, view.version))
    }
    prevHeadRef.current = view.version
  }, [view])

  const onSelectVersion = useCallback(
    (v: number) => {
      if (!view) return
      if (v === view.version) {
        setSelected(null)
        setSnapshot(null)
        return
      }
      setSelected(v)
      reportAPI.getReportVersionView(reportId, v).then((res) => setSnapshot(res.data))
    },
    [reportId, view],
  )

  if (error) return <div className="mx-auto max-w-3xl p-8 text-destructive">{error}</div>
  if (!view) return <div className="mx-auto max-w-3xl p-8 text-muted-foreground">Loading report…</div>

  const scrubbing = selected != null && snapshot != null
  return (
    <ReportViewer
      view={scrubbing ? snapshot : view}
      versions={view.versions}
      selected={scrubbing ? (selected as number) : view.version}
      headVersion={view.version}
      isLive={isLive && !scrubbing}
      writingSection={scrubbing ? null : writingSection}
      changedIds={scrubbing ? [] : changedIds}
      contentVersion={scrubbing ? (selected as number) : view.version}
      onSelectVersion={onSelectVersion}
    />
  )
}
